import { useCallback, useEffect, useState } from "react";
import { Check, Clock, Copy, Mail, Minus, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/lib/permissions";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { InboundEmailsList } from "@/components/InboundEmailsList";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { formatDateTime } from "@/lib/format";
import { InfoHint } from "@/components/InfoHint";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  TH,
  TableShell,
} from "@/components/PageUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type TabValue = "emails" | "waiting" | "allowed";

type AllowedSender = components["schemas"]["BillAllowedSenderResponse"];
type Rejection = components["schemas"]["BillForwardingRejectionResponse"];
type AuthVerdict = components["schemas"]["EmailAuthResult"];

// Alignment-aware on the backend: "passed" means tied to the sender's own domain, not just any pass.
const VERDICT_META: Record<
  AuthVerdict,
  { Icon: typeof Check; className: string; describe: (name: string) => string }
> = {
  Pass: {
    Icon: Check,
    className: "text-emerald-600 dark:text-emerald-400",
    describe: (name) => `${name} passed. Verified against the sender's domain.`,
  },
  Fail: {
    Icon: X,
    className: "text-destructive",
    describe: (name) => `${name} failed. The check did not pass for the sender's domain.`,
  },
  Unknown: {
    Icon: Minus,
    className: "text-muted-foreground",
    describe: (name) => `${name} couldn't be checked. There was no usable ${name} result on the message.`,
  },
};

function AuthCheck({ name, verdict }: { name: string; verdict: AuthVerdict }) {
  const { Icon, className, describe } = VERDICT_META[verdict];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-default items-center gap-1 text-xs">
          <Icon className={`size-3.5 ${className}`} />
          <span className="text-muted-foreground">{name}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{describe(name)}</TooltipContent>
    </Tooltip>
  );
}

// "Unknown" (often forwarders stripping signing) is worded apart from a hard fail, so we don't accuse a real sender.
function authFailureCopy(auth: AuthVerdict): string {
  return auth === "Fail"
    ? "This sender is allowed, but the message failed email authentication (DKIM/DMARC). It may be a spoof, or a forwarding setup that breaks signing. Adding them again won't help; they need to send in a way that passes authentication."
    : "This sender is allowed, but we couldn't verify this message's authentication. There was no DKIM signature or SPF result we could tie to their domain (often a forwarding setup that strips signing). Adding them again won't help; they need to send in a way that passes authentication.";
}

function CopyButton({ value, className }: { value: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleCopy}
          className={`inline-flex items-center justify-center text-muted-foreground transition-colors hover:text-foreground ${className ?? ""}`}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied!" : "Copy"}</TooltipContent>
    </Tooltip>
  );
}

const MANAGE_REASON = "Only owners and admins can change who may forward bills";

function isSenderAllowed(r: Rejection, allowed: AllowedSender[]): boolean {
  return allowed.some(
    (s) =>
      (s.kind === "Address" && s.value === r.fromAddress) ||
      (s.kind === "Domain" && s.value === r.fromDomain),
  );
}

/** Only forwards from allowed senders are imported; the rest are held under "Senders waiting". See eng-docs "AP - Bill Forwarding". */
export function EmailForwardingPage({
  onBillClick,
}: {
  /** Called with a bill id when a user opens a bill created from a forwarded email. */
  onBillClick?: (billId: string) => void;
} = {}) {
  const api = useDolfinClient();
  // Viewing the lists is open to every member; changing the allowlist is Owner/Admin only (the backend 403s).
  const { can, demoReason } = usePermissions();
  const canManage = can("manageBillForwarding");

  const [address, setAddress] = useState<string | null>(null);
  const [addressLoading, setAddressLoading] = useState(true);
  const [addressError, setAddressError] = useState<string | null>(null);

  const [allowed, setAllowed] = useState<AllowedSender[] | null>(null);
  const [waiting, setWaiting] = useState<Rejection[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [newSender, setNewSender] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await api.GET("/v1/bills/inbound-email-address");
      if (cancelled) return;
      if (error || !data?.address) setAddressError("Failed to load your inbound email address.");
      else setAddress(data.address);
      setAddressLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const refreshLists = useCallback(async () => {
    const [sendersRes, rejectionsRes] = await Promise.all([
      api.GET("/v1/bills/allowed-senders"),
      api.GET("/v1/bills/forwarding-rejections"),
    ]);
    if (sendersRes.error || !sendersRes.data) {
      setListError("Failed to load allowed senders.");
      return;
    }
    setAllowed(sendersRes.data);
    setWaiting(rejectionsRes.error || !rejectionsRes.data ? [] : rejectionsRes.data);
    setListError(null);
  }, [api]);

  useEffect(() => {
    void refreshLists();
  }, [refreshLists]);

  async function addSender(rawValue: string) {
    const value = rawValue.trim().toLowerCase();
    if (!value) return;
    // Infer the kind: an "@" means a full email address, otherwise a whole domain.
    const kind = value.includes("@") ? "Address" : "Domain";

    setAdding(true);
    const { data, error } = await api.POST("/v1/bills/allowed-senders", { body: { kind, value } });
    setAdding(false);
    if (error || !data) {
      toast.error(apiErrorMessage(error, "Couldn't allow that sender."));
      return;
    }
    setNewSender("");
    toast.success(`Allowed ${value}`);
    void refreshLists();
  }

  async function removeSender(id: string, value: string) {
    setBusyId(id);
    const { error } = await api.DELETE("/v1/bills/allowed-senders/{id}", { params: { path: { id } } });
    setBusyId(null);
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't remove that sender."));
      return;
    }
    setAllowed((prev) => prev?.filter((s) => s.id !== id) ?? prev);
    toast.success(`Removed ${value}`);
    void refreshLists();
  }

  // Waiting rows whose sender is now allowed drop off the list (their future forwards will import).
  const visibleWaiting = waiting && allowed ? waiting.filter((r) => !isSenderAllowed(r, allowed)) : waiting;
  const waitingCount = visibleWaiting?.length ?? 0;

  // Land on held senders if any, else the log. Chosen once, so allowing the last sender doesn't switch tabs.
  const [tab, setTab] = useState<TabValue | null>(null);
  useEffect(() => {
    if (tab === null && visibleWaiting !== null) setTab(visibleWaiting.length > 0 ? "waiting" : "emails");
  }, [tab, visibleWaiting]);
  const activeTab = tab ?? "emails";

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Email forwarding"
        subtitle="Forward bills to one address and they are extracted and turned into bills automatically."
      />

      <section className="rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl border bg-muted/50">
              <Mail className="size-5 text-muted-foreground" />
            </span>
            <div>
              <div className="font-semibold">Your inbound bill address</div>
              {addressLoading ? (
                <span className="text-xs text-muted-foreground">Loading...</span>
              ) : addressError ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive">
                  <span className="size-1.5 rounded-full bg-destructive" /> Unavailable
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                  <span className="size-1.5 rounded-full bg-emerald-500" /> Receiving
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          {addressLoading ? (
            <div className="flex items-center justify-center rounded-lg border bg-muted/30 py-4">
              <Spinner className="size-4 text-muted-foreground" />
            </div>
          ) : addressError ? (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {addressError}
            </p>
          ) : address ? (
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3">
              <code className="flex-1 truncate font-mono text-sm">{address}</code>
              <CopyButton value={address} />
            </div>
          ) : null}
        </div>

        <p className="mt-3 text-sm text-pretty text-muted-foreground">
          Set up a forwarding rule in your inbox, or forward bills one at a time. Only forwards from allowed
          senders are imported. Your own team members are always allowed; anything else is held under
          Senders waiting.
        </p>

        <dl className="mt-5 grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border">
          <Stat label="Allowed senders" value={allowed === null ? "-" : allowed.length} hint="Plus your own team" />
          <Stat
            label="Senders waiting"
            value={visibleWaiting === null ? "-" : waitingCount}
            tone={waitingCount > 0 ? "amber" : undefined}
          />
        </dl>
      </section>

      <div className="mt-6 mb-4">
        <Tabs value={activeTab} onValueChange={(v) => setTab(v as TabValue)}>
          <TabsList>
            <TabsTrigger value="emails">Inbound emails</TabsTrigger>
            <TabsTrigger value="waiting">
              Senders waiting{waitingCount > 0 ? ` (${waitingCount})` : ""}
            </TabsTrigger>
            <TabsTrigger value="allowed">
              Allowed senders{allowed && allowed.length > 0 ? ` (${allowed.length})` : ""}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {activeTab === "emails" ? (
        <InboundEmailsList onBillClick={onBillClick} />
      ) : activeTab === "waiting" ? (
        <div className="space-y-3">
          <p className="text-sm text-pretty text-muted-foreground">
            Bills forwarded from senders that are not allowed yet were held. Allowing a sender imports their{" "}
            <span className="text-foreground">future</span> forwards. The held ones are not kept, so ask them
            to re-send afterwards.
          </p>
          {listError ? (
            <ErrorState message={listError} onRetry={() => void refreshLists()} />
          ) : visibleWaiting === null ? (
            <LoadingState label="Loading senders..." />
          ) : visibleWaiting.length === 0 ? (
            <EmptyState icon={Clock} title="Nothing waiting">
              No bills have been held from unknown senders.
            </EmptyState>
          ) : (
            <TableShell>
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Sender</TableHead>
                    <TableHead className={TH}>Received</TableHead>
                    <TableHead className={TH}>
                      <span className="sr-only">Action</span>
                    </TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {visibleWaiting.map((r) => {
                    const count = Number(r.attachmentCount);
                    const authFailed = r.reason === "AuthenticationFailed";
                    return (
                      <TableRow key={r.id} className="align-top">
                        <TableCell className="max-w-[26rem]">
                          <EntityCell
                            name={r.fromAddress}
                            secondary={
                              <>
                                {r.subject || "(no subject)"}
                                {count > 0 && ` · ${count} attachment${count === 1 ? "" : "s"}`}
                              </>
                            }
                            badges={
                              authFailed ? (
                                <Badge variant="destructive" className="shrink-0">
                                  {r.authResult === "Fail" ? "Failed authentication" : "Unverified"}
                                </Badge>
                              ) : null
                            }
                          />
                          {authFailed && (
                            <div className="mt-1.5 flex items-center gap-3 pl-11">
                              <AuthCheck name="DKIM" verdict={r.dkimResult} />
                              <AuthCheck name="SPF" verdict={r.spfResult} />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                          {formatDateTime(r.receivedAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          {authFailed ? (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              Already allowed
                              <InfoHint label="Why this sender is still held">{authFailureCopy(r.authResult)}</InfoHint>
                            </span>
                          ) : (
                            <PermissionGuard allowed={canManage} reason={MANAGE_REASON} demoReason={demoReason("manageBillForwarding")}>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={adding}
                                onClick={() => void addSender(r.fromAddress)}
                              >
                                Allow sender
                              </Button>
                            </PermissionGuard>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableShell>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <p className="max-w-lg text-sm text-pretty text-muted-foreground">
              Email addresses or whole domains permitted to forward bills in. Your organisation's own users
              are always allowed and are not listed here.
            </p>
            <form
              className="flex w-full max-w-md items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                void addSender(newSender);
              }}
            >
              <Input
                value={newSender}
                placeholder="accounts@supplier.com or supplier.com"
                aria-label="Email address or domain to allow"
                disabled={adding || !canManage}
                onChange={(e) => setNewSender(e.target.value)}
                className="h-9 font-mono text-sm"
              />
              <PermissionGuard allowed={canManage} reason={MANAGE_REASON} demoReason={demoReason("manageBillForwarding")}>
                <Button type="submit" size="sm" disabled={adding || !newSender.trim()}>
                  {adding ? <Spinner className="size-4" /> : <Plus className="size-4" />}
                  Allow
                </Button>
              </PermissionGuard>
            </form>
          </div>

          {listError ? (
            <ErrorState message={listError} onRetry={() => void refreshLists()} />
          ) : allowed === null ? (
            <LoadingState label="Loading allowed senders..." />
          ) : allowed.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="No extra senders yet">
              Your team members can already forward bills. Add a supplier's address or domain to let them send
              bills here directly.
            </EmptyState>
          ) : (
            <TableShell>
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Sender</TableHead>
                    <TableHead className={TH}>Type</TableHead>
                    <TableHead className={TH}>
                      <span className="sr-only">Remove</span>
                    </TableHead>
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {allowed.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-3">
                          <code className="truncate font-mono text-sm">{s.value}</code>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.kind === "Domain" ? "Whole domain" : "Email address"}
                      </TableCell>
                      <TableCell className="text-right">
                        <PermissionGuard allowed={canManage} reason={MANAGE_REASON} demoReason={demoReason("manageBillForwarding")}>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Remove ${s.value}`}
                            disabled={busyId === s.id}
                            onClick={() => void removeSender(s.id, s.value)}
                          >
                            {busyId === s.id ? <Spinner className="size-4" /> : <Trash2 className="size-4" />}
                          </Button>
                        </PermissionGuard>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          )}
        </div>
      )}
    </PageContainer>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "amber";
}) {
  return (
    <div className="bg-card px-4 py-3">
      <dt className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">{label}</dt>
      <dd className={cn("mt-1 text-xl font-bold tabular-nums", tone === "amber" && "text-amber-600")}>
        {value}
        {hint ? <span className="ml-2 text-xs font-normal text-muted-foreground">{hint}</span> : null}
      </dd>
    </div>
  );
}
